<script lang="ts" setup>
import { computed } from 'vue';

import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  InfoIconPosition,
  SuggestionTypeSelector,
  SuggestionTypeSelectorSize,
} from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import { FlightFilterType } from '@apps/main/new-desktop/core/stores/filters/flightFilter.model';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import {
  FlightsFiltersEventType,
  FlightsFilterType,
} from '@apps/main/new-desktop/modules/flightsFilters';
import type { FlightsFilterEvents } from '@apps/main/new-desktop/modules/flightsFilters';
import {
  FilterType,
  FilterUpdateEventType,
} from '@apps/main/new-desktop/modules/filters';

import type { TagData } from '@libs/ui';
import { AppCheckbox, AppPreferencesTags } from '@libs/ui';

import type {
  ItineraryFlightLoaderSegment,
  ItineraryFlightSegment,
} from '../../../models';
import {
  FlightPriceType,
  isItineraryFlightLoaderSegment,
} from '../../../models';

const statsigEventsStore = useStatsigEventsStore();
const flightsFiltersStore = useFlightsFiltersStore();
const browserStore = useBrowserStore();

interface FlightSegmentHeaderProps {
  segment: ItineraryFlightSegment | ItineraryFlightLoaderSegment;
  printMode: boolean;
}

const props = defineProps<FlightSegmentHeaderProps>();

interface AvailableAccommodationSegmentEmits {
  (eventName: 'remove-filter', id: string): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
}

const emit = defineEmits<AvailableAccommodationSegmentEmits>();

const showOrderDropdown = computed<boolean>(
  () =>
    props.segment.suggestionType !== undefined &&
    (isItineraryFlightLoaderSegment(props.segment) ||
      (props.segment.productCardData !== null &&
        props.segment.productCardData.details.flightPriceType !==
          FlightPriceType.IncludedOnOutbound))
);

const directFlightOnly = computed<boolean>(() =>
  props.segment.filtersPills.some(
    (filter) =>
      filter.type === FlightFilterType.MaxNumberOfStops && filter.value === 0
  )
);

function onDirectFlightOnlyChange(value: boolean): void {
  statsigEventsStore.logTripOverviewDirectFlightOnlyCheckboxClicked();
  const flight: FlightsFilterEvents = {
    eventType: FlightsFiltersEventType.UpdateFilter,
    filterType: FlightsFilterType.NumberOfStops,
    data: {
      eventType: FilterUpdateEventType.UpdateFilter,
      filterType: FilterType.RadioButton,
      selected: 0,
      ingredientId: props.segment.ingredientId,
    },
  };
  if (value) {
    flightsFiltersStore.update(flight);
  } else {
    flight.data.selected = undefined;
    flightsFiltersStore.update(flight);
  }
}
const filters = computed<TagData[]>(() =>
  props.segment.filtersPills.filter(
    (pill) =>
      pill.type !== FlightFilterType.MaxNumberOfStops || pill.value !== 0
  )
);

const slotContentWrapperWidth = computed<string>(() =>
  browserStore.isSmallerThan1200px ? '100%' : 'auto'
);

const suggestionTypeSelectorProps = computed(() =>
  browserStore.isSmallerThan1200px
    ? {
        fontVariant: 'font-bold' as const,
        appSelectType: 'default' as const,
        iconName: 'list' as const,
        showInfoIcon: InfoIconPosition.Inside,
        size: SuggestionTypeSelectorSize.Small,
        class: ['suggestion-type-selector-mobile'],
      }
    : {
        fontVariant: 'font-light' as const,
        isDirectFlightCheckboxExperiment: false,
        appSelectType: 'transparent' as const,
        showInfoIcon: InfoIconPosition.None,
        size: SuggestionTypeSelectorSize.Normal,
        class: ['suggestion-type-selector'],
      }
);
</script>

<template>
  <div
    class="available-flight-segment-header direct-flight-experiment"
    :class="[{ 'alternative-grid': browserStore.isSmallerThan1200px }]"
  >
    <template v-if="!props.printMode">
      <AppPreferencesTags
        :tags="filters"
        :total-tags="props.segment.filtersPills.length"
        :add-preferences-text="$t('filter.add_filters')"
        should-hide-add-button
        class="preferences-tags"
        @show-more-filters="emit('show-more-filters')"
        @add-filter="
          statsigEventsStore.logTripOverviewFilterButtonClicked();
          emit('show-more-filters');
        "
        @remove-filter="emit('remove-filter', $event)"
      >
        <template #after-add-button>
          <div
            :class="[
              'direct-flight-only-checkbox-wrapper',
              {
                'direct-flight-options-wrapper':
                  browserStore.isSmallerThan1200px,
              },
            ]"
          >
            <div
              class="direct-flight-only-checkbox"
              :class="{
                'no-border': !(
                  showOrderDropdown &&
                  props.segment.suggestionType !== undefined
                ),
              }"
            >
              <AppCheckbox
                :id="`direct-flight-${
                  browserStore.isSmallerThan1200px ? 'mobile' : 'desktop'
                }-${props.segment.ingredientId}`"
                size="small"
                gap="spacing-12"
                :checked="directFlightOnly"
                theme="secondary"
                :class="{
                  'mobile-checkbox': browserStore.isSmallerThan1200px,
                }"
                @change="onDirectFlightOnlyChange"
              >
                {{ $t('filter.flightFilters.direct_flight_only') }}
              </AppCheckbox>
            </div>

            <SuggestionTypeSelector
              v-if="
                showOrderDropdown && props.segment.suggestionType !== undefined
              "
              v-bind="suggestionTypeSelectorProps"
              :suggestion-type="props.segment.suggestionType"
              icon-position="right"
              @suggestion-type-selected="
                emit('suggestion-type-selected', $event)
              "
            />
          </div>
        </template>
      </AppPreferencesTags>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.available-flight-segment-header {
  width: 100%;
  display: grid;
  grid-template-columns: min-content min-content;
  grid-template-rows: auto;
  align-items: center;
  padding-left: var(--spacing-8);
  .direct-flight-only-checkbox-wrapper {
    display: flex;
    align-items: center;
    padding-left: var(--spacing-4);
    @include for-less-or-equal-than-1200-px {
      padding-left: 0;
    }

    .direct-flight-only-checkbox {
      width: max-content;
      border-right: 1px solid var(--color-scrollbar);
      padding-right: var(--spacing-12);

      &.no-border {
        border-right: none;
        padding-right: 0;
      }
    }

    &.direct-flight-options-wrapper {
      justify-content: space-between;
      width: 100%;
      gap: var(--spacing-16);
      flex-direction: row-reverse;

      .direct-flight-only-checkbox {
        border-right: none;
        padding-right: 0;
        margin-left: 0;
      }
    }
  }

  .suggestion-type-selector {
    margin: var(--spacing-8) var(--spacing-16) 0;
    align-self: flex-start;
    z-index: 1;
    align-items: center;
    display: flex;
    justify-content: center;
  }

  .preferences-tags {
    grid-column: 3/4;
    grid-row: 1/-1;
    margin-right: var(--spacing-16);
  }
  :deep(.slot-content-wrapper) {
    width: v-bind(slotContentWrapperWidth);
  }
  @include for-less-or-equal-than-1200-px {
    &.alternative-grid {
      grid-template-columns: min-content 1fr;
      margin-left: 0;

      .suggestion-type-selector-mobile {
        grid-column: 1/2;
        grid-row: 1/2;
        margin: 0;
        @include font-label-s-bold;
      }

      .preferences-tags {
        grid-column: 2/3;
        justify-content: flex-end;
      }
    }
  }
}
.direct-flight-experiment {
  padding-left: var(--spacing-12);
  @include for-less-or-equal-than-599-px {
    padding-left: 0;
  }
  .suggestion-type-selector {
    margin: auto;
    @include for-more-or-equal-than-1200-px {
      margin-top: var(--spacing-2);
    }
  }
}
</style>
